import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';
import AppLogo from '@/components/AppLogo';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <AppLogo size="sm" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Privacy Policy</h1>
            <p className="text-sm text-slate-500">Effective Date: February 18, 2026</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <p className="lead text-slate-600">
            For Get Me Not ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services (collectively, the "Service").
          </p>
          <p className="text-slate-600">
            Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Information We Collect</h2>
          
          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">1.1 Information You Provide</h3>
          <p className="text-slate-600">When you use For Get Me Not, you may provide us with the following types of information:</p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>Account Information:</strong> Email address and password when you create an account</li>
            <li><strong>Personal Documents:</strong> Documents, images, and files you choose to upload and store</li>
            <li><strong>Financial Records:</strong> Receipts, reimbursement records, and expense information you enter</li>
            <li><strong>Health Notes:</strong> Medical notes, medication records, and health-related information you choose to store</li>
            <li><strong>Contact Information:</strong> Names, phone numbers, addresses, and other contact details you add</li>
            <li><strong>Vehicle Information:</strong> Vehicle details, maintenance records, warranty information, and related documents</li>
            <li><strong>Calendar and Dates:</strong> Appointments, birthdays, reminders, and other date-related information</li>
          </ul>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">1.2 Information Collected Automatically</h3>
          <p className="text-slate-600">When you access the Service, we may automatically collect:</p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>Device Information:</strong> Device type, operating system, and unique device identifiers</li>
            <li><strong>Usage Data:</strong> Features accessed, time spent in the app, and interaction patterns</li>
            <li><strong>Technical Logs:</strong> Error reports and performance data to improve the Service</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="text-slate-600">We use the information we collect solely to:</p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li>Provide, maintain, and improve the Service</li>
            <li>Process and store the content you create and upload</li>
            <li>Send you service-related notifications and reminders you have configured</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Detect, prevent, and address technical issues and security concerns</li>
            <li>Comply with legal obligations</li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4 my-6">
            <p className="text-green-800 font-medium mb-2">We Do NOT:</p>
            <ul className="list-disc pl-6 text-green-700 space-y-1">
              <li>Sell your personal information to third parties</li>
              <li>Use your data for advertising purposes</li>
              <li>Track your activity across other applications or websites</li>
              <li>Share your content with other users without your consent</li>
            </ul>
          </div>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Data Sharing and Disclosure</h2>
          <p className="text-slate-600">We may share your information only in the following limited circumstances:</p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>Service Providers:</strong> With trusted third-party vendors who assist us in operating the Service (e.g., cloud storage providers, payment processors), subject to confidentiality obligations</li>
            <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or governmental request</li>
            <li><strong>Protection of Rights:</strong> To protect the rights, property, or safety of For Get Me Not, our users, or others</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with notice to you</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Data Storage and Security</h2>
          <p className="text-slate-600">
            Your data is stored using secure cloud storage infrastructure. We implement reasonable administrative, technical, and physical safeguards designed to protect your information from unauthorized access, disclosure, alteration, or destruction.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-6">
            <p className="text-amber-800">
              <strong>Security Disclaimer:</strong> While we strive to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </div>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Your Rights and Choices</h2>
          <p className="text-slate-600">
            Depending on your location, you may have certain rights regarding your personal information under applicable data protection laws, including the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA).
          </p>
          
          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">5.1 Your Rights Include:</h3>
          <ul className="list-disc pl-6 text-slate-600 space-y-2">
            <li><strong>Right to Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Right to Deletion:</strong> Request deletion of your personal information, subject to certain exceptions</li>
            <li><strong>Right to Data Portability:</strong> Request an export of your data in a commonly used format</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for processing where consent is the legal basis</li>
            <li><strong>Right to Object:</strong> Object to certain types of processing of your personal information</li>
          </ul>

          <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">5.2 Exercising Your Rights</h3>
          <p className="text-slate-600">
            To exercise any of these rights, please contact us at <strong>privacy@forgetmenot.app</strong>. We will respond to your request within the timeframe required by applicable law (typically 30 days).
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Important Health Information Disclaimer</h2>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 my-6">
            <p className="text-red-800 font-medium mb-3">HIPAA DISCLAIMER</p>
            <ul className="list-disc pl-6 text-red-700 space-y-2">
              <li>For Get Me Not is <strong>NOT a healthcare provider</strong> and is <strong>NOT HIPAA compliant</strong></li>
              <li>The Service is not intended to store Protected Health Information (PHI) as defined under HIPAA</li>
              <li>Any medical notes, medication records, or health-related information you choose to store is done so <strong>at your own risk</strong></li>
              <li>This app is <strong>NOT a substitute</strong> for professional medical record systems, electronic health records (EHR), or healthcare provider documentation</li>
              <li>Users assume full responsibility for any health-related information stored in the app</li>
              <li>Do not store information that requires HIPAA-level protection in this application</li>
            </ul>
          </div>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Children's Privacy</h2>
          <p className="text-slate-600">
            The Service is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <strong>privacy@forgetmenot.app</strong>. If we discover that we have collected personal information from a child under 13, we will promptly delete such information.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Data Retention</h2>
          <p className="text-slate-600">
            We retain your personal information for as long as your account is active or as needed to provide you with the Service. You may request deletion of your account and associated data at any time. Upon account deletion, we will delete or anonymize your personal information within 30 days, except where retention is required by law.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. International Data Transfers</h2>
          <p className="text-slate-600">
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using the Service, you consent to the transfer of your information to the United States and other countries where we operate.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Changes to This Privacy Policy</h2>
          <p className="text-slate-600">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. Your continued use of the Service after any changes constitutes your acceptance of the updated Privacy Policy.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. Contact Us</h2>
          <p className="text-slate-600">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
          </p>
          <div className="bg-slate-100 rounded-xl p-4 my-4">
            <p className="text-slate-700">
              <strong>For Get Me Not</strong><br />
              Email: <a href="mailto:privacy@forgetmenot.app" className="text-blue-600 hover:underline">privacy@forgetmenot.app</a><br />
              Subject Line: Privacy Inquiry
            </p>
          </div>

          <hr className="my-8 border-slate-200" />

          <p className="text-sm text-slate-500 mt-8">
            Last Updated: February 18, 2026
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-slate-500">
          <p>© 2026 For Get Me Not. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-slate-700">Privacy Policy</a>
            <span>•</span>
            <a href="/terms" className="hover:text-slate-700">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
